/**
 * Configuration d'affichage des champs extraits, PARTAGÉE entre :
 *  - la review GOD (`CGReviewPage` → `FieldEditList`)
 *  - l'étape de vérification agence (`FormCGIA/v2/VerifyInfoScreen` via
 *    `buildVerifyGroups`)
 *
 * Centralise les libellés humains des `docType`, l'ordre de priorité métier des
 * pastilles de provenance, et la liste ordonnée des champs par groupe
 * (Véhicule / Vendeur / Acheteur / Adresse / Co-titulaire). Garder ces
 * définitions à un seul endroit évite que les deux vues divergent.
 */

import type { NatureEntite } from '../FormCGIA/validation/personneMorale';

// ─────────────────────────────────────────────────────────────
//  Mapping docType → libellé humain
// ─────────────────────────────────────────────────────────────

export const DOC_TYPE_LABELS: Record<string, string> = {
  carte_grise: 'Carte grise',
  cerfa_cession: 'Certificat de cession',
  cerfa_demande_immat: "Demande d'immatriculation",
  mandat: "Mandat d'immatriculation",
  assurance: 'Attestation assurance',
  controle_technique: 'Contrôle technique',
  piece_identite: "Pièce d'identité titulaire — RECTO",
  piece_identite_verso: "Pièce d'identité titulaire — VERSO",
  piece_identite_hebergeur: "Pièce d'identité hébergeur — RECTO",
  piece_identite_hebergeur_verso: "Pièce d'identité hébergeur — VERSO",
  piece_identite_cotitulaire: "Pièce d'identité co-titulaire — RECTO",
  piece_identite_cotitulaire_verso: "Pièce d'identité co-titulaire — VERSO",
  permis_conduire: 'Permis de conduire titulaire — RECTO',
  permis_conduire_verso: 'Permis de conduire titulaire — VERSO',
  permis_conduire_cotitulaire: 'Permis de conduire co-titulaire — RECTO',
  permis_conduire_cotitulaire_verso: 'Permis de conduire co-titulaire — VERSO',
  kbis: 'Kbis',
  kbis_vendeur: 'Kbis du vendeur',
  kbis_acheteur: "Kbis de l'acquéreur",
  justif_domicile: 'Justificatif de domicile',
  attestation_hebergement: "Attestation d'hébergement",
  recepisse_da: 'Récépissé de dépôt',
  unknown: 'Type inconnu',
};

export const humanizeDocType = (t: string): string => DOC_TYPE_LABELS[t] ?? t;

// ─────────────────────────────────────────────────────────────
//  Priorité métier d'affichage des pastilles de provenance
//  (carte grise d'abord — pièce de référence d'un dossier CTVO)
// ─────────────────────────────────────────────────────────────

export const DOC_TYPE_PRIORITY: string[] = [
  'carte_grise',
  'controle_technique',
  'cerfa_cession',
  'cerfa_demande_immat',
  'mandat',
  'assurance',
  'piece_identite',
  'piece_identite_verso',
  'permis_conduire',
  'permis_conduire_verso',
  'piece_identite_cotitulaire',
  'piece_identite_cotitulaire_verso',
  'permis_conduire_cotitulaire',
  'permis_conduire_cotitulaire_verso',
  'justif_domicile',
  'attestation_hebergement',
  'kbis',
  'kbis_acheteur',
  'kbis_vendeur',
];

export const docTypePriorityRank = (t: string): number => {
  const idx = DOC_TYPE_PRIORITY.indexOf(t);
  return idx === -1 ? DOC_TYPE_PRIORITY.length : idx;
};

// ─────────────────────────────────────────────────────────────
//  Définition d'un champ affichable
// ─────────────────────────────────────────────────────────────

export interface FieldDef {
  fieldKey: string;
  label: string;
  /**
   * Clés OCR de repli, par priorité décroissante. La valeur affichée vient de
   * la PREMIÈRE clé qui porte une valeur — l'ordre encode la règle métier
   * (ex : Vendeur = titulaire carte grise, le certificat de cession n'est
   * qu'un secours si la CG n'a pas pu être lue). Les chips de provenance
   * agrègent les sources de toutes les clés candidates.
   */
  fallbackFieldKeys?: string[];
  /**
   * Pièce de référence métier pour ce champ. Quand renseignée et qu'une source
   * de ce docType existe parmi les candidates, on n'affiche QUE cette chip.
   */
  preferredDocType?: string;
  /**
   * Visibilité conditionnelle selon la nature de l'entité (vendeur / acheteur) :
   *  - 'particulier' : civilité / nom / prénom / date+lieu de naissance… ;
   *  - 'pm' (personne morale) : raison sociale + SIRET (14 chiffres).
   * `undefined` = toujours affiché. Le tri se fait dans `buildGroup` d'après
   * `vendeur_est_personne_morale` / `acheteur_est_personne_morale`. Évite
   * d'afficher « Civilité / Prénom » pour une société ou « SIRET » pour un
   * particulier.
   */
  entityKind?: 'particulier' | 'pm';
}

export const VEHICULE_FIELDS: FieldDef[] = [
  { fieldKey: 'immatriculation', label: 'Immatriculation', preferredDocType: 'carte_grise' },
  { fieldKey: 'vin', label: 'VIN', preferredDocType: 'carte_grise' },
  { fieldKey: 'numero_formule', label: 'N° de formule' },
  { fieldKey: 'date_premiere_mec', label: '1ère mise en circulation' },
  { fieldKey: 'marque', label: 'Marque', preferredDocType: 'carte_grise' },
  // Fusion date_vente + heure_vente en un seul champ datetime-local — le rendu
  // est piloté côté FieldEditRow via la propriété `pairedFieldKey`.
  { fieldKey: 'date_vente', label: 'Date et heure de la vente' },
];

// Vendeur = titulaire actuel de la carte grise (information réglementaire).
// Le certificat de cession — souvent manuscrit, OCR fragile — n'est qu'un REPLI.
// Champs particulier vs personne morale filtrés par `vendeur_est_personne_morale`.
// NB : pour une personne morale, l'identifiant attendu par le SIV/ANTS est le
// SIRET (14 chiffres, cf. `vendeur_siret` de la cession), PAS le SIREN (9) du
// C.1 de la carte grise.
export const VENDEUR_FIELDS: FieldDef[] = [
  {
    fieldKey: 'titulaire_civilite',
    label: 'Civilité',
    fallbackFieldKeys: ['vendeur_civilite'],
    preferredDocType: 'carte_grise',
    entityKind: 'particulier',
  },
  {
    fieldKey: 'titulaire_nom',
    label: 'Nom',
    fallbackFieldKeys: ['vendeur_nom'],
    preferredDocType: 'carte_grise',
    entityKind: 'particulier',
  },
  {
    fieldKey: 'titulaire_prenom',
    label: 'Prénom(s)',
    fallbackFieldKeys: ['vendeur_prenom'],
    preferredDocType: 'carte_grise',
    entityKind: 'particulier',
  },
  {
    fieldKey: 'titulaire_nom_usage',
    label: "Nom d'usage",
    preferredDocType: 'carte_grise',
    entityKind: 'particulier',
  },
  {
    fieldKey: 'raison_sociale',
    label: 'Raison sociale',
    fallbackFieldKeys: ['vendeur_raison_sociale', 'titulaire_nom'],
    preferredDocType: 'carte_grise',
    entityKind: 'pm',
  },
  {
    fieldKey: 'vendeur_siret',
    label: 'SIRET',
    preferredDocType: 'cerfa_cession',
    entityKind: 'pm',
  },
];

export const ACHETEUR_FIELDS: FieldDef[] = [
  { fieldKey: 'civilite', label: 'Civilité', entityKind: 'particulier' },
  { fieldKey: 'nom', label: 'Nom', entityKind: 'particulier' },
  { fieldKey: 'prenom', label: 'Prénom(s)', entityKind: 'particulier' },
  { fieldKey: 'nom_usage', label: "Nom d'usage", entityKind: 'particulier' },
  { fieldKey: 'date_naissance', label: 'Date de naissance', entityKind: 'particulier' },
  { fieldKey: 'lieu_naissance', label: 'Lieu de naissance', entityKind: 'particulier' },
  // Extrait par l'OCR (PI, souvent inféré depuis le lieu) et soumis à MiSIV via
  // DepartmentOfBirth : il doit donc être relu/corrigeable ici comme les autres
  // champs de naissance (règle : tout champ pré-rempli par l'IA est validable).
  { fieldKey: 'departement_naissance', label: 'Département de naissance', entityKind: 'particulier' },
  { fieldKey: 'pays_naissance', label: 'Pays de naissance', entityKind: 'particulier' },
  {
    fieldKey: 'acheteur_raison_sociale',
    label: 'Raison sociale',
    fallbackFieldKeys: ['raison_sociale'],
    preferredDocType: 'cerfa_cession',
    entityKind: 'pm',
  },
  // Le Kbis (`siret`) fait foi ; la cession (`acheteur_siret`) n'est qu'un repli
  // quand aucun Kbis n'a été lu.
  { fieldKey: 'siret', label: 'SIRET', fallbackFieldKeys: ['acheteur_siret'], entityKind: 'pm' },
  { fieldKey: 'telephone', label: 'Téléphone' },
];

export const ADRESSE_FIELDS: FieldDef[] = [
  { fieldKey: 'numero_voie', label: 'Numéro' },
  { fieldKey: 'extension_voie', label: 'Extension' },
  { fieldKey: 'type_voie', label: 'Type de voie' },
  { fieldKey: 'nom_voie', label: 'Nom de voie' },
  { fieldKey: 'lieu_dit', label: 'Lieu-dit' },
  { fieldKey: 'etage_escalier', label: 'Étage / esc. / appt.' },
  { fieldKey: 'complement_adresse', label: 'Complément' },
  { fieldKey: 'boite_postale', label: 'Boîte postale' },
  { fieldKey: 'code_postal', label: 'Code postal' },
  { fieldKey: 'ville', label: 'Ville' },
  { fieldKey: 'pays', label: 'Pays' },
];

export const COTITULAIRE_FIELDS: FieldDef[] = [
  { fieldKey: 'cotitulaire_civilite', label: 'Civilité' },
  { fieldKey: 'cotitulaire_nom', label: 'Nom' },
  { fieldKey: 'cotitulaire_prenom', label: 'Prénom(s)' },
  { fieldKey: 'cotitulaire_nom_usage', label: "Nom d'usage" },
];

/**
 * Filtre les champs d'un groupe (Vendeur / Acheteur) selon la nature de l'entité,
 * telle que la tranche la règle unique `natureVendeur` / `natureAcheteur`
 * (validation/personneMorale.ts) — la MÊME que celle qui pilote les pièces
 * réclamées, pour que l'écran et la checklist ne puissent plus se contredire.
 *
 *  - `'pm'`          → champs société + non tagués ;
 *  - `'particulier'` → champs identité + non tagués ;
 *  - `'indecis'`     → TOUT, les deux blocs.
 *
 * `'indecis'` affiche plus, jamais moins. Quand la cession déclare un
 * particulier alors qu'un SIRET a été lu, personne ne peut savoir de l'extérieur
 * qui a raison : masquer l'un des deux blocs revient à cacher au relecteur
 * précisément le champ qu'il devait corriger. Sur GP-482-JC le SIRET du vendeur
 * PROCELL n'était visible NULLE PART, ni pour l'agence ni pour le god, alors que
 * c'est lui qui partait en base.
 */
export const filterFieldsByEntityKind = (defs: FieldDef[], nature: NatureEntite): FieldDef[] => {
  if (nature === 'indecis') return defs;
  return defs.filter((d) => !d.entityKind || d.entityKind === nature);
};

// Map de "paired fields" : la clé principale embarque la valeur d'une 2ᵉ clé
// qui ne sera PAS rendue séparément (fusion date_vente + heure_vente en input
// datetime-local).
export const PAIRED_FIELDS: Record<string, string> = {
  date_vente: 'heure_vente',
};

/**
 * Champ affiché du bloc Acheteur → clé OCR correspondante sur le Cerfa 13750.
 *
 * Sur la demande d'immatriculation, `titulaire_*` désigne le NOUVEAU titulaire,
 * c'est-à-dire l'ACQUÉREUR (alors que sur la carte grise c'est le vendeur). Le
 * 13750 est donc une source légitime de l'identité de l'acheteur, mais rangée
 * sous d'autres clés que celles du bloc (`nom`, `prenom`…) : aucune pastille de
 * provenance n'y pointait, alors que c'est LA pièce à recouper avec la PI et le
 * permis (demande Luc 2026-07-29).
 *
 * `nom_usage`, `pays_naissance`, raison sociale et SIRET sont absents du 13750 →
 * volontairement hors de cette map. `telephone` et les champs d'adresse en sont
 * aussi absents car le 13750 les émet sous LEURS clés canoniques : la pastille y
 * apparaît déjà naturellement par l'agrégation.
 */
export const DEMANDE_IMMAT_ACHETEUR_FIELD_KEYS: Record<string, string> = {
  civilite: 'titulaire_civilite',
  nom: 'titulaire_nom',
  prenom: 'titulaire_prenom',
  date_naissance: 'titulaire_date_naissance',
  lieu_naissance: 'titulaire_lieu_naissance',
  departement_naissance: 'titulaire_departement_naissance',
};

/**
 * Champ affiché du bloc Co-titulaire → clé OCR correspondante sur la PIÈCE
 * D'IDENTITÉ du co-titulaire.
 *
 * Symétrique de `DEMANDE_IMMAT_ACHETEUR_FIELD_KEYS`, et pour exactement la même
 * raison. La CNI du co-titulaire est bien déposée, analysée et routée (slot
 * `piece_identite_cotitulaire`), mais le prompt d'extraction est partagé avec
 * celui de la CNI de l'acquéreur : elle sort ses champs sous les clés
 * GÉNÉRIQUES `nom` / `prenom` / `civilite` / `nom_usage`. Or ces clés-là sont
 * retirées de l'agrégation pour ce slot par `EXCLUDED_FIELD_SOURCES` — garde
 * volontaire et nécessaire, sans laquelle l'identité du co-titulaire écrase
 * celle du titulaire et part à l'ANTS (CY-517-VW, DM-724-VQ).
 *
 * Conséquence : aucune source ne pointait vers cette pièce depuis le bloc
 * Co-titulaire, dont les champs n'affichaient que la « Demande
 * d'immatriculation ». Le relecteur voyait la CNI dans le visualiseur sans
 * aucun lien pour y sauter depuis le champ à contrôler (retour François
 * 2026-08-04, FV-624-CC).
 *
 * On ne touche NI la valeur affichée NI son arbitrage : cette table ne sert
 * qu'à poser une pastille de référence cliquable, exactement comme le 13750
 * sur le bloc Acheteur.
 *
 * Seul le RECTO est visé : sur une CNI ancien format le verso ne porte ni nom
 * ni prénom (la MRZ est au recto, cf. skill CTVO §13.14).
 */
export const PIECE_IDENTITE_COTITULAIRE_FIELD_KEYS: Record<string, string> = {
  cotitulaire_civilite: 'civilite',
  cotitulaire_nom: 'nom',
  cotitulaire_prenom: 'prenom',
  cotitulaire_nom_usage: 'nom_usage',
};

/**
 * La pièce porte-t-elle une valeur exploitable pour ce champ ? C'est la
 * condition « quand c'est cohérent » : on ne pose la pastille de référence que
 * si le document dit effectivement quelque chose sur ce champ — sinon elle
 * renverrait le relecteur vers un document muet sur ce point.
 *
 * `rawFields` est le `fields` brut de l'extraction (shape
 * `{ [key]: { value, confidence, source } }`), tel que stocké côté store
 * (`ocrResponse.fields`) comme côté backend (`cg_ocr_extractions.fields`).
 */
const refDocCoversField = (rawFields: unknown, ocrKey: string | undefined): boolean => {
  if (!ocrKey || !rawFields || typeof rawFields !== 'object') return false;
  const entry = (rawFields as Record<string, unknown>)[ocrKey];
  if (!entry || typeof entry !== 'object') return false;
  const value = (entry as { value?: unknown }).value;
  return value !== null && value !== undefined && String(value).trim() !== '';
};

/** Le 13750 porte-t-il une valeur exploitable pour ce champ du bloc Acheteur ? */
export const demandeImmatCoversField = (rawFields: unknown, fieldKey: string): boolean =>
  refDocCoversField(rawFields, DEMANDE_IMMAT_ACHETEUR_FIELD_KEYS[fieldKey]);

/** La CNI du co-titulaire porte-t-elle une valeur pour ce champ du bloc Co-titulaire ? */
export const pieceIdentiteCotitulaireCoversField = (rawFields: unknown, fieldKey: string): boolean =>
  refDocCoversField(rawFields, PIECE_IDENTITE_COTITULAIRE_FIELD_KEYS[fieldKey]);

export const fmtValue = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  return String(v);
};
